import { Controller } from "@hotwired/stimulus"
import Uppy from "@uppy/core"
import AwsS3Multipart from "@uppy/aws-s3"
import { FileChecksum } from "@rails/activestorage/src/file_checksum"

export default class extends Controller {
  static targets = ["input", "fileList", "progress", "progressBar", "dropzone", "signedIdInput"]
  static values = {
    videoTooLong: String,
    videoTooLarge: String,
    videoLoadError: String,
    createUrl: String,
    signPartUrl: String,
    completeUrl: String,
    abortUrl: String,
    maxDuration: Number,
    maxFileSize: Number
  }

  connect() {
    this.form = this.element.closest("form")
    this.handleFormSubmit = this.handleFormSubmit.bind(this)
    this.boundHandleVisit = this.boundHandleVisit.bind(this)

    this.form?.addEventListener("submit", this.handleFormSubmit)
    document.addEventListener("turbo:before-visit", this.boundHandleVisit)

    this.initializeUppy()
  }

  disconnect() {
    document.removeEventListener("turbo:before-visit", this.boundHandleVisit)
    this.form?.removeEventListener("submit", this.handleFormSubmit)
    if (this.uppy) {
      this.uppy.cancelAll()
      this.uppy.resetProgress()
      this.uppy.off()
    }
  }

  triggerFileInput() {
    this.inputTarget.click()
  }

  async handleFileSelect() {
    const file = this.inputTarget.files[0]
    if (!file) return

    if (this.hasMaxFileSizeValue && file.size > this.maxFileSizeValue) {
      alert(this.videoTooLargeValue || this.videoLoadErrorValue || "파일이 너무 큽니다.")
      this.removeFile()
      return
    }

    if (file.type?.startsWith("video/")) {
      const isValid = await this.validateVideoDuration(file)
      if (!isValid) return
    }

    this.showFileUI(file)
    await this.startUpload(file)
  }

  removeFile() {
    this.clearUppyFiles()
    this.inputTarget.value = null
    this.clearSignedId()
    this.fileListTarget.innerHTML = ""
    this.resetProgress()
    this.progressTarget.classList.add("hidden")
    this.inputTarget.disabled = false

    this.dispatch("fileRemoved")
  }

  boundHandleVisit(event) {
    if (event.detail.url !== window.location.href) {
      this.removeFile()
    }
  }

  handleDragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-blue-500", "bg-blue-50")
  }

  handleDragLeave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-blue-500", "bg-blue-50")
  }

  handleDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-blue-500", "bg-blue-50")

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.inputTarget.files = files
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  async startUpload(file) {
    this.resetProgress()
    this.clearSignedId()

    try {
      const checksum = await this.calculateChecksum(file)
      this.clearUppyFiles()
      this.uppy.addFile({
        name: file.name,
        type: file.type,
        size: file.size,
        data: file,
        meta: {
          checksum
        }
      })
      await this.uppy.upload()
    } catch (error) {
      this.handleUploadError(error)
    }
  }

  initializeUppy() {
    this.uppy = new Uppy({
      autoProceed: false,
      allowMultipleUploads: false,
      restrictions: {
        maxNumberOfFiles: 1
      }
    })

    this.uppy.use(AwsS3Multipart, {
      limit: 2,
      shouldUseMultipart: true,
      // S3 제약(5MB 이상, 최대 1만 파트)을 만족하도록 최소 파트 크기 지정
      getChunkSize: file => Math.max(Math.ceil(file.size / 10_000), 20 * 1024 * 1024),
      createMultipartUpload: this.createMultipartUpload.bind(this),
      signPart: this.signPart.bind(this),
      listParts: this.listParts.bind(this),
      completeMultipartUpload: this.completeMultipartUpload.bind(this),
      abortMultipartUpload: this.abortMultipartUpload.bind(this)
    })

    this.uppy.on("upload", () => {
      this.progressTarget.classList.remove("hidden")
      this.dispatchDirectUploadEvent("direct-uploads:start")
    })

    this.uppy.on("upload-progress", (_file, progress) => {
      if (progress?.percentage != null) {
        this.progressBarTarget.style.width = `${progress.percentage}%`
      }
    })

    this.uppy.on("upload-success", file => {
      if (file.meta?.signedId) {
        this.setSignedId(file.meta.signedId)
      }
      this.dispatchDirectUploadEvent("direct-uploads:end")
    })

    this.uppy.on("error", error => {
      this.handleUploadError(error)
      this.dispatchDirectUploadEvent("direct-uploads:error")
    })

    this.uppy.on("cancel-all", () => {
      this.resetProgress()
    })
  }

  clearUppyFiles() {
    this.uppy.cancelAll()
    this.uppy.getFiles().forEach(file => {
      this.uppy.removeFile(file.id)
    })
    this.uppy.resetProgress()
  }

  async createMultipartUpload(file) {
    const payload = {
      filename: file.name,
      byte_size: file.size,
      checksum: file.meta.checksum,
      content_type: file.type
    }

    const data = await this.postJson(this.createUrlValue, payload)
    file.meta.signedId = data.signed_id

    return {
      key: data.key,
      uploadId: data.upload_id || data.uploadId
    }
  }

  async signPart(file, { uploadId, key, partNumber, body, signal }) {
    const data = await this.postJson(this.signPartUrlValue, {
      upload_id: uploadId,
      key,
      part_number: partNumber,
      content_length: body.size,
      signed_id: file.meta.signedId
    }, signal)

    return {
      url: data.url,
      headers: data.headers,
      method: "PUT"
    }
  }

  // 복구(resume)를 사용하지 않으므로 빈 배열 반환
  listParts() {
    return []
  }

  async completeMultipartUpload(file, { uploadId, key, parts, signal }) {
    const data = await this.postJson(this.completeUrlValue, {
      upload_id: uploadId,
      key,
      parts,
      signed_id: file.meta.signedId
    }, signal)

    file.meta.signedId = data.signed_id

    return {
      location: data.location || data.key || key
    }
  }

  async abortMultipartUpload(file, { uploadId }) {
    if (!uploadId || !file.meta?.signedId) return

    await fetch(this.abortUrlValue, {
      method: "DELETE",
      credentials: "same-origin",
      headers: this.fetchHeaders(),
      body: JSON.stringify({
        upload_id: uploadId,
        signed_id: file.meta.signedId
      })
    })
  }

  async validateVideoDuration(file) {
    const limitSeconds = this.hasMaxDurationValue ? this.maxDurationValue : 7200

    return new Promise(resolve => {
      const video = document.createElement("video")
      video.preload = "metadata"

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src)
        const duration = video.duration

        if (duration > limitSeconds) {
          this.removeFile()
          alert(this.videoTooLongValue)
          resolve(false)
          return
        }

        resolve(true)
      }

      video.onerror = () => {
        window.URL.revokeObjectURL(video.src)
        this.removeFile()
        alert(this.videoLoadErrorValue)
        resolve(false)
      }

      video.src = URL.createObjectURL(file)
    })
  }

  showFileUI(file) {
    this.fileListTarget.innerHTML = `
      <div class="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md">
        <span class="text-sm text-gray-700">${file.name}</span>
        <button
          type="button"
          class="text-red-500 hover:text-red-700 transition-colors duration-200"
          data-action="click->uppy-upload#removeFile"
        >
          &times;
        </button>
      </div>
    `
  }

  resetProgress() {
    this.progressBarTarget.style.width = "0%"
  }

  clearSignedId() {
    if (this.hasSignedIdInputTarget) {
      this.signedIdInputTarget.value = ""
    }
  }

  setSignedId(signedId) {
    if (this.hasSignedIdInputTarget) {
      this.signedIdInputTarget.value = signedId
    }
  }

  async calculateChecksum(file) {
    return new Promise((resolve, reject) => {
      FileChecksum.create(file, (error, checksum) => {
        if (error) {
          reject(error)
        } else {
          resolve(checksum)
        }
      })
    })
  }

  handleUploadError(error) {
    console.error(error)
    alert(error.message || this.videoLoadErrorValue || "업로드 중 문제가 발생했습니다.")
    this.clearSignedId()
    this.resetProgress()
    this.progressTarget.classList.add("hidden")
    this.inputTarget.disabled = false
  }

  async postJson(url, body, signal) {
    const response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: this.fetchHeaders(),
      body: JSON.stringify(body),
      signal
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      const message = data.error || "업로드 요청에 실패했습니다."
      throw new Error(message)
    }

    return data
  }

  fetchHeaders() {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": token
    }
  }

  dispatchDirectUploadEvent(name) {
    if (!this.form) return
    const event = new CustomEvent(name, { bubbles: true })
    this.form.dispatchEvent(event)
  }

  handleFormSubmit(event) {
    const hasFile = this.inputTarget.files?.length > 0
    const hasSignedId = this.hasSignedIdInputTarget && this.signedIdInputTarget.value

    if (hasFile && !hasSignedId) {
      event.preventDefault()
      alert("업로드 완료 후 제출해주세요.")
      return
    }

    if (hasFile) {
      this.inputTarget.disabled = true
    }
  }
}
